import classNames from "classnames";
import React, {ReactNode} from "react";


import "./ProgressCircle.css"


export type ProgressCircleStatus = 'success' | 'warning' | 'error' | 'danger' | 'empty' | 'upgrade';

export type ProgressCircleSegment = {
    /**
     * Segment length in percents (0-100)
     */
    percentage: number;
    /**
     * Color of the segment stroke
     */
    status: ProgressCircleStatus;
};

export type ProgressCircleProps = {
    className?: string;
    /**
     * Custom color override when `status` is not used
     */
    size?: number;
    children?: ReactNode;
    circleThickness?: 3 | 5 | 15
    emptyPercentage?: number
    segments: ProgressCircleSegment[];
};

const getColorByStatus = (status: ProgressCircleStatus) => {
    switch (status) {
        case "success":
            return 'var(--lgc-palette-success-80)';
        case "warning":
            return 'var(--lgc-palette-accent-40)';
        case "error":
            return 'var(--lgc-palette-danger-80)';
        case "danger":
            return 'var(--lgc-palette-danger-50)';
        case "upgrade":
            return 'var(--lgc-palette-accent-60)';
        case "empty":
            return 'var(--lgc-palette-neutral-variant-95)';
    }
}

export const ProgressCircle = React.forwardRef<HTMLDivElement, ProgressCircleProps>(({
    className,
    size = 24,
    circleThickness = 5,
    children,
    segments,
}: ProgressCircleProps, ref) => {
    const strokeWidth = circleThickness;
    const radius = (size - strokeWidth) / 2;
    const circleLength = 2 * Math.PI * radius;

    const renderSegments = () => {
        if (!segments) return null;

        let currentOffset = 0;

        return segments.map((seg, idx) => {
            const segLength = circleLength * (seg.percentage / 100);
            if (!segLength) {
                return null;
            }
            // Clamp the trailing gap: when segments add up to 100%, float error can make it
            // slightly negative, which invalidates the dasharray and paints a solid ring.
            const dasharray = `0 ${currentOffset} ${segLength} ${Math.max(0, circleLength - segLength - currentOffset)}`;
            currentOffset += segLength;

            return (
                <circle
                    key={idx}
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                    fill="none"
                    stroke={getColorByStatus(seg.status)}
                    strokeDasharray={dasharray}
                    strokeDashoffset={0}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            );
        });
    };

    return (
        <div
            ref={ref}
            className={classNames(
                "ProgressCircle",
                className,
            )}
            style={{
                width: size,
                height: size,
            }}
        >
            <svg
                className="ProgressCircle__svg"
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                xmlns="http://www.w3.org/2000/svg"
            >
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="none"
                />
                {renderSegments()}
            </svg>
            <div className="ProgressCircle__content">
                {children}
            </div>
        </div>
    );
});

ProgressCircle.displayName = "ProgressCircle";
